import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Script from 'next/script';
import trustedFormConfig from '../../Data/Maps/trustedFormConfig';
import {
  isHiddenTrustedFormComponentPresent,
  isHiddenTrustedFormQuestionOnFuturePage,
  isTrustedFormCertIdAssignedToHiddenQuestion,
} from './trustedFormLoader.helpers';
import { parseSessionDataAndPopulateFormFields } from '../../Hooks/useBrowserMessages/useBrowserMessagesHelpers';
import { addConsentCertificationRecord } from '../../Lib/redux/slices/trustedForm';
import { incrementMetric } from '../../Services/Datadog';

const TrustedFormLoader = () => {
  const components = useSelector((store) => store.components);
  const dispatch = useDispatch();
  const iFrameId = useSelector((store) => store.optionalFormConfig.iFrameId);
  const countryCode = useSelector((store) => store.formConfig.countryCode);
  const formId = useSelector((store) => store.formConfig.formId);
  const consentCertification = useSelector(
    (store) => store.trustedForm.consentCertification
  );
  const isTrustedFormLoggingEnabled = useSelector(
    (store) => store.trustedForm.isTrustedFormLoggingEnabled
  );
  const currentPage = useSelector((store) => store.navigation.currentPage);
  const [trustedFormLoadAttempted, setTrustedFormLoadAttempted] =
    useState(false);
  const [trustedFormCertId, setTrustedFormCertId] = useState(undefined);
  const [trustedFormBootstrapScriptUrl, setTrustedFormBootstrapScriptUrl] =
    useState(undefined);
  const [
    hasTFScriptBeenLoadingForTooLong,
    setHasTFScriptBeenLoadingForTooLong,
  ] = useState(false);
  const hasTFScriptLoadedSuccessfully = useRef(false);
  const hasTFScriptFailedToLoad = useRef(false);
  const haveTrustedFormCertsLoaded = useRef(false);
  const tfLoadRetryAttempt = useRef(0);
  const [
    hasTrustedFormCertIdAssignmentBeenDeferred,
    setHasTrustedFormCertIdAssignmentBeenDeferred,
  ] = useState(false);
  const [impendingRetry, setImpendingRetry] = useState(false);
  const isEnvProductionEu = process.env.NEXT_PUBLIC_ENV === 'production-eu';
  const assignTrustedFormCertIdToHiddenQuestion = (embeddedCertificateId) => {
    if (isTrustedFormLoggingEnabled) {
      // eslint-disable-next-line no-console
      console.log(
        'MVF Info - Trusted Form Cert ID assigned from within form embed: ',
        embeddedCertificateId
      );
    }
    parseSessionDataAndPopulateFormFields({
      sessionData: { 'Trusted Form': embeddedCertificateId },
      components,
      dispatch,
      iFrameId,
      countryCode,
      formId,
    });
  };

  const kickOffTFRetryAttemptToLoadTrustedFormAssets = () => {
    // Increment Retry Attempt Counter
    tfLoadRetryAttempt.current += 1;
    removeTrustedFormBootstrapScript();
    setTrustedFormBootstrapScriptUrl(undefined);
    setImpendingRetry(true);
  };

  useEffect(() => {
    if (impendingRetry === true) {
      if (isTrustedFormLoggingEnabled) {
        console.log(
          'MVF App Info - Retry loading Trusted Form assets ... at: ',
          Date.now()
        );
      }
      setImpendingRetry(false);
      setTimeout(() => {
        if (
          hasTFScriptLoadedSuccessfully.current === false ||
          haveTrustedFormCertsLoaded.current === false
        ) {
          // This will re-initiate the useEffect that is responsible for loading the TF assets
          setTrustedFormLoadAttempted(false);
          setHasTFScriptBeenLoadingForTooLong(false);
          hasTFScriptFailedToLoad.current = false;
          hasTFScriptLoadedSuccessfully.current = false;
        }
      }, trustedFormConfig.loadRetryAttemptTimingMap[tfLoadRetryAttempt.current]);
    }
  }, [impendingRetry]);

  useEffect(() => {
    if (trustedFormLoadAttempted === false && !isEnvProductionEu) {
      // Define TF load success callback handler
      window.trustedFormCertIdCallback = (embeddedCertificateId) => {
        /*
         *  Step 2 of 2 - Assign the embedded page's trusted form cert to the
         *                store's trusted form HTML input component value
         */
        haveTrustedFormCertsLoaded.current = Boolean(embeddedCertificateId); // Resolves to true if the cert is present and not empty

        // eslint-disable-next-line prefer-const
        const tfCertAssignmentCallbackTimeoutId = setTimeout(() => {
          if (
            isHiddenTrustedFormComponentPresent(components, [
              trustedFormConfig.mappedAdmiralQuestionId,
              trustedFormConfig.questionText,
            ])
          ) {
            if (
              isHiddenTrustedFormQuestionOnFuturePage(currentPage, components, [
                trustedFormConfig.mappedAdmiralQuestionId,
                trustedFormConfig.questionText,
              ])
            ) {
              setHasTrustedFormCertIdAssignmentBeenDeferred(true);
              setTrustedFormCertId(embeddedCertificateId);
            } else {
              assignTrustedFormCertIdToHiddenQuestion(embeddedCertificateId);
            }
          }

          const consentCertificationRecord = {
            provider: 'trustedForm',
            location: 'form',
            id: embeddedCertificateId,
          };

          const consentCertificationAlreadyExists = consentCertification.some(
            (existingConsentCertificationRecord) => {
              return (
                existingConsentCertificationRecord.id === embeddedCertificateId
              );
            }
          );

          if (!consentCertificationAlreadyExists) {
            dispatch(
              addConsentCertificationRecord({
                consentCertificationRecord,
              })
            );
          }
          clearTimeout(tfCertAssignmentCallbackTimeoutId);
        }, 1000);
      };

      // The originalLoadAttemptTracker tracks which TF load attempt relates to the timeout watcher we're about to setup below
      const originalLoadAttemptTracker = tfLoadRetryAttempt.current;

      /* Set up a 15-second latency threshold after which we will unmount the
       * TrustedForm Script loader if it failed to load within that time, provided
       * that the load attempt didn't fail for different reasons (e.g. a TF API
       * request failure - i.e. triggering the onError prop)
       */
      const tfLoadLatencyThresholdTimeoutId = setTimeout(() => {
        if (
          hasTFScriptLoadedSuccessfully.current === false &&
          hasTFScriptFailedToLoad.current === false
        ) {
          /* If the current load attempt is not the same as the original attempt
           * that relates to this timeout watcher, then that request never timed
           * out (it failed fast instead) and we need to skip its timeout callback logic.
           * When there are multiple timeout callbacks in play, this boolean comparison
           * recouples timeouts with their related initiating load attempts which is crucial
           * when managing script tag reloads reliably. This is because neither script tags
           * nor Next.Script components come with a timeout handler.
           */
          if (tfLoadRetryAttempt.current === originalLoadAttemptTracker) {
            setHasTFScriptBeenLoadingForTooLong(true);
            if (isTrustedFormLoggingEnabled) {
              console.log(
                'MVF App Info - Trusted Form Bootstrap script took too long to load (over 15 seconds)',
                '- Removing the script tag to abort - Current Attempt was:',
                tfLoadRetryAttempt.current + 1,
                'of',
                trustedFormConfig.maxRetryAttempts + 1
              );
            }
          }
        }
        clearTimeout(tfLoadLatencyThresholdTimeoutId);
      }, trustedFormConfig.slowNetworkResilientLatency);

      /*
       * Step 1 of 2 - Asynchronously load and mount trusted form script into embedded Chameleon app DOM
       */
      const trustedFormUrl =
        // eslint-disable-next-line prefer-template
        'https://cdn.trustedform.com/bootstrap.js?field=' +
        escape('xxTrustedFormCertUrl') +
        '&l=' +
        new Date().getTime() +
        Math.random();
      setTrustedFormBootstrapScriptUrl(trustedFormUrl);
      setTrustedFormLoadAttempted(true);
    }
  }, [trustedFormLoadAttempted, consentCertification]);

  useEffect(() => {
    /*
     * The operation of this well-guarded effect will run when a page change is about
     * to begin (if and only if) the TrustedForm Cert Id Assignment was deferred.
     * This will allow the store-update of the highly re-render triggering
     * 'components' Redux slice to take place whilst the UI is undergoing a page
     * transition meaning that UI components are not actively used by users.
     * NOTE: The 'components' slice is subscribed to (via useSelector) by many
     *       non-null rendering UI components and therefore has the impact of
     *       causing re-renders in many places.
     */
    const isHiddenTrustedFormComponentPresentButWithoutCurrentCert =
      isHiddenTrustedFormComponentPresent(components, [
        trustedFormConfig.mappedAdmiralQuestionId,
        trustedFormConfig.questionText,
      ]) &&
      !isTrustedFormCertIdAssignedToHiddenQuestion(
        trustedFormCertId,
        components,
        [
          trustedFormConfig.mappedAdmiralQuestionId,
          trustedFormConfig.questionText,
        ]
      );
    const isTrustedFormCertIdPresentAndHasCertAssignmentBeenDeferred =
      trustedFormCertId && hasTrustedFormCertIdAssignmentBeenDeferred;
    if (
      !isEnvProductionEu &&
      isTrustedFormCertIdPresentAndHasCertAssignmentBeenDeferred &&
      currentPage > 0 &&
      isHiddenTrustedFormComponentPresentButWithoutCurrentCert
    ) {
      // Assign the TrustedForm certificate id to the hidden TrustedForm question
      setHasTrustedFormCertIdAssignmentBeenDeferred(false);
      assignTrustedFormCertIdToHiddenQuestion(trustedFormCertId);
    }
  }, [currentPage]);

  /* TF Script Loader Removal
   * To fully remove a Next <Script/> component it is not enough to unmount
   * the component because it has created a separate HTML script tag DOM node
   * that will not automatically get removed.
   * Instead, we additionally need to find and remove the DOM node explicitly.
   * The setTimeout of 0 ensures that this DOM deletion happens after the React unmounts by rendering null.
   */
  const removeTrustedFormBootstrapScript = () => {
    const bootstrapScriptTag = document.querySelector(
      '#mvfTrustedFormBootstrapScript'
    );
    bootstrapScriptTag?.remove();
  };

  useEffect(() => {
    if (hasTFScriptBeenLoadingForTooLong) {
      incrementMetric({
        name: 'loadTrustedFormBootstrapScript',
        tags: {
          type: 'requestTimeout',
          status: 'TrustedForm script failed to load',
          retryAttempt: tfLoadRetryAttempt.current,
        },
      });
      const timeoutId = setTimeout(() => {
        removeTrustedFormBootstrapScript();
        if (tfLoadRetryAttempt.current < trustedFormConfig.maxRetryAttempts) {
          kickOffTFRetryAttemptToLoadTrustedFormAssets();
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [hasTFScriptBeenLoadingForTooLong]);

  const shouldRenderTrustedFormScript =
    impendingRetry === false &&
    trustedFormBootstrapScriptUrl &&
    !hasTFScriptBeenLoadingForTooLong;

  return shouldRenderTrustedFormScript ? (
    <Script
      src={trustedFormBootstrapScriptUrl}
      id="mvfTrustedFormBootstrapScript"
      strategy="lazyOnload" // Ensures an asynchronous / non-blocking loading mechanism
      onLoad={() => {
        const bootstrapScriptTag = document.querySelector(
          '#mvfTrustedFormBootstrapScript'
        );
        if (bootstrapScriptTag) {
          hasTFScriptLoadedSuccessfully.current = true;
          setTimeout(() => {
            if (haveTrustedFormCertsLoaded.current === false) {
              if (isTrustedFormLoggingEnabled) {
                console.log(
                  'MVF App Info - Trusted Form Bootstrap Script Runtime error - Failed to create & register Trusted Form certificate within 15 seconds',
                  '- Removing the script tag to abort - Current Attempt was:',
                  tfLoadRetryAttempt.current + 1,
                  'of',
                  trustedFormConfig.maxRetryAttempts + 1
                );
              }
              incrementMetric({
                name: 'createTrustedFormCertificate',
                tags: {
                  type: 'requestFailure',
                  status: 'TrustedForm cert failed to load',
                  retryAttempt: tfLoadRetryAttempt.current,
                },
              });
              if (
                tfLoadRetryAttempt.current < trustedFormConfig.maxRetryAttempts
              ) {
                kickOffTFRetryAttemptToLoadTrustedFormAssets();
              }
            } else {
              incrementMetric({
                name: 'createTrustedFormCertificate',
                tags: {
                  type: 'success',
                  status: 'TrustedForm loaded successfully',
                  retryAttempt: tfLoadRetryAttempt.current,
                },
              });
            }
          }, trustedFormConfig.slowNetworkResilientLatency);
        } else {
          if (isTrustedFormLoggingEnabled) {
            console.log(
              'MVF App Info - GET request for Trusted Form script completed successfully but took too long - Trusted Form script tag was already removed'
            );
          }
          if (tfLoadRetryAttempt.current < trustedFormConfig.maxRetryAttempts) {
            kickOffTFRetryAttemptToLoadTrustedFormAssets();
          }
        }
      }}
      onError={() => {
        hasTFScriptFailedToLoad.current = true;
        const bootstrapScriptTag = document.querySelector(
          '#mvfTrustedFormBootstrapScript'
        );
        if (bootstrapScriptTag) {
          if (isTrustedFormLoggingEnabled) {
            console.log(
              'MVF App Info - Failed to load Trusted Form script - Current Attempt was:',
              tfLoadRetryAttempt.current + 1,
              'of',
              trustedFormConfig.maxRetryAttempts + 1
            );
          }
          removeTrustedFormBootstrapScript();
          incrementMetric({
            name: 'loadTrustedFormBootstrapScript',
            tags: {
              type: 'requestFailure',
              status: 'TrustedForm script failed to load',
              retryAttempt: tfLoadRetryAttempt.current,
            },
          });
        } else if (isTrustedFormLoggingEnabled) {
          console.log(
            'MVF App Info - GET request failure response for Trusted Form script but it took too long - Trusted Form script tag was already removed'
          );
        }
        if (tfLoadRetryAttempt.current < trustedFormConfig.maxRetryAttempts) {
          kickOffTFRetryAttemptToLoadTrustedFormAssets();
        }
      }}
    />
  ) : null;
};

export default TrustedFormLoader;
